import { AnchorXDTO } from '@model/dynamicTimeTable/AnchorXDTO';
import { AnchorYDTO } from '@model/dynamicTimeTable/AnchorYDTO';
import { ColorDTO } from '@model/dynamicTimeTable/ColorDTO';
import {
  AnnouncementDTO,
  AnnouncementItemDTO,
  AnnouncementRichTextContentDTO,
  ImageResizeType,
} from '@/model';

// Everything about the announcement item that is NOT the rich text editor's
// own content (that lives in the editor's `content` state instead) or the
// item's persisted range/schedule fields. Assembled from two sources — see
// toAnnouncementItemEditorState/fromAnnouncementItemEditorState below for
// the load/save mapping.
export interface AnnouncementItemEditorState {
  name: string;
  template: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
  imageResizeType: ImageResizeType;
  backgroundHorizontalAlign: AnchorXDTO;
  backgroundVerticalAlign: AnchorYDTO;
  backgroundColor: ColorDTO;
  imageId?: number;
  file?: string;
  fileName?: string;
}

// Builds the editor's local state from the item being edited (its own
// content fields) and the announcement (the render size/color/alignment
// mirrored from its shared Element — available on AnnouncementDTO itself,
// independently of how many items it has, unlike the same fields on
// AnnouncementItemDTO). Keeping this in one place means load and save can't
// quietly drift apart on which field comes from which source.
export const toAnnouncementItemEditorState = (
  existingItem: AnnouncementItemDTO | undefined,
  fetchedAnnouncement: AnnouncementDTO | undefined,
): AnnouncementItemEditorState => ({
  name: existingItem?.name ?? '',
  template: existingItem?.template ?? false,
  x: existingItem?.offsetX ?? 10,
  y: existingItem?.offsetY ?? 10,
  width: Number(fetchedAnnouncement?.width),
  height: Number(fetchedAnnouncement?.height),
  imageResizeType: fetchedAnnouncement?.imageResizeType ?? ImageResizeType.FIX,
  backgroundHorizontalAlign: fetchedAnnouncement?.anchorX ?? AnchorXDTO.LEFT,
  backgroundVerticalAlign: fetchedAnnouncement?.anchorY ?? AnchorYDTO.MIDDLE,
  backgroundColor: fetchedAnnouncement?.backgroundColor ?? ColorDTO.FFF_White,
  imageId: existingItem?.imageId,
  file: existingItem?.file,
});

interface AnnouncementItemContent {
  plainTextContent: string;
  richTextContent: AnnouncementRichTextContentDTO;
  textToSpeech: string;
}

// Assembles the single save payload from the editor state, so the caller
// doesn't have to know which fields go where. `baseItem` carries the
// id/range/schedule fields — for a brand-new item those come from the range
// modal rather than from a previously loaded item, but either way a full
// item must be supplied by the caller before saving. width/height are
// intentionally left out — they're display-only, mirrored from the Element
// and never written back through the item save.
export const fromAnnouncementItemEditorState = (
  editorState: AnnouncementItemEditorState,
  content: AnnouncementItemContent,
  baseItem: AnnouncementItemDTO,
  announcementId: number,
): AnnouncementItemDTO => ({
  ...baseItem,
  announcementId,
  name: editorState.name,
  template: editorState.template,
  imageId: editorState.imageId,
  file: editorState.file,
  plainTextContent: content.plainTextContent,
  richTextContent: content.richTextContent,
  textToSpeech: content.textToSpeech,
  offsetX: editorState.x,
  offsetY: editorState.y,
  backgroundColor: editorState.backgroundColor,
  imageResizeType: editorState.imageResizeType,
  anchorX: editorState.backgroundHorizontalAlign,
  anchorY: editorState.backgroundVerticalAlign,
});
